// ===== Packages =====

import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
}                                       from 'react';
import {
    useNavigate,
    useLocation,
    useSearchParams,
}                                       from 'react-router-dom';
import { ReactSVG }                     from 'react-svg';
import {
    verifyPasswordResetCode,
    confirmPasswordReset,
    applyActionCode,
    signInWithEmailAndPassword,
    signInWithCustomToken,
}                                       from 'firebase/auth';
import {
    FirebaseError,
}                                       from 'firebase/app';
import TaiPasswordStrength              from 'tai-password-strength';
import PasswordValidator                from 'password-validator';

// ===== Firebase =====

import fire                             from '../../fire';

// ===== Components =====

import Spinner                          from '../Spinner';
import Button                           from '../Editor/helpers/Button';
import PixelInput                       from '../PixelInput';

// ===== Services =====

import {
    getAuthErrorMessage,
    playAudio,
    recordUserAction,
    sendResetPasswordEmail,
    sendVerifyAccountEmail,
    unsubscribeByMailTrackingLink,
    getBookInviteClaimPreview,
    getSocialAutomationClaimPreview,
    claimBookInvite,
    updatePageTitle,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
    isTestMode,
    getVerifyEmailRequestErrorMessage,
    syncEmailVerificationStatus,
} from '../../services';
import useLazyAudioRef                     from '../../services/useLazyAudioRef';

// ===== Enums =====

import {
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
    PAGE_ROUTE,
    BUTTON_TYPE,
    AUTH_ACTION_STATUS_TYPE,
    AUTH_ACTION_MODE_TYPE,
    AUTH_ACTION_QUERY_PARAM,
    AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE,
    AUTHENTICATION_ERROR_CODE,
    PASSWORD_STRENGTH,
    RESET_PASSWORD_CALLABLE_ERROR_CODE,
    RESET_PASSWORD_CALLABLE_ERROR_REASON,
    UNSUBSCRIBE_BY_MAIL_TRACKING_LINK_ERROR_CODE,
    UNSUBSCRIBE_TYPE,
    BOOK_INVITE_CLAIM_PREVIEW_STATUS,
    SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS,
    SOCIAL_AUTOMATION_PLATFORM,
    BOOK_INVITE_CLAIM_CALLABLE_ERROR_REASON,
    USER_ACTION_TYPE,
    READER_PARAMS_TYPE,
    SEND_VERIFY_ACCOUNT_EMAIL_STATUS,
}                                       from '../../enums';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    DEFAULT_SNACKBAR_VISIBLE_DURATION,
    DEFAULT_AUDIO_VOLUME,
    HOVER_TARGET_CLASSNAME,
    SNACKBAR_MESSAGE_VERIFY_EMAIL_SUCCESS,
    SNACKBAR_MESSAGE_VERIFY_EMAIL_RECENT_NEW_ACCOUNT,
    SUPPORT_EMAIL_ADDRESS,
}                                       from '../../constants/generalConstants';
import FONT_TYPE                        from '../../constants/fontType';
import CURSOR_SIGN                      from '../../constants/cursorSigns';

// ===== Images =====

import VerascopeLogo                    from '../../images/verascope-logo-silhouette-dark.svg';
import VerascopeLogomark                from '../../images/verascope-logomark.svg';
import EnvelopeIcon                     from '../../images/envelope.svg';
import CautionIcon                      from '../../images/caution.svg';

// ===== Sounds =====

import Success                          from '../../sounds/message_success.mp3';

// ===== Styles =====

import {
    Container,
    ContentContainer,
    LogomarkContainer,
    StatusHeader,
    StatusMessage,
    FormContainer,
    ClaimCode,
    ClaimCarouselCard,
    ClaimCarouselContainer,
    ClaimCarouselDotButton,
    ClaimCarouselDots,
    ClaimCarouselNavButton,
    ClaimCarouselNavRow,
    ClaimDetailItem,
    ClaimDetailLabel,
    ClaimDetailsContainer,
    ClaimDetailValue,
    PasswordStrengthContainer,
    PasswordStrengthBar,
    ActionContainer,
    LoadingContainer,
}                                       from './styles';
import {
    PageLogo,
    PageSubtitle,
    PageTitle,
}                                       from '../../styles';
import { theme }                        from '../../themes/theme-context';

// ===== Interfaces =====

import {
    IApplyVerifyEmailActionCodeOnceInput,
    ISnackbarItem,
    IPasswordError,
    ISocialAutomationClaimPreviewResult,
    IVerifyEmailActionCodeAttempt,
}                                       from '../../interfaces';

// ===== Initialize Firebase =====

const { auth } = fire();
const DEFAULT_SOCIAL_CLAIM_POLL_INTERVAL_MS = 5000;
const VERIFY_EMAIL_SESSION_REFRESH_TIMEOUT_MS = 5000;

const verifyEmailActionCodeAttempts = new Map<string, IVerifyEmailActionCodeAttempt>();

/**
 * Applies a one-time email verification code once per browser tab so localhost
 * StrictMode remounts cannot consume the same Firebase action code twice.
 */
const applyVerifyEmailActionCodeOnce = async ({
    authInstance,
    oobCode,
}: IApplyVerifyEmailActionCodeOnceInput): Promise<void> => {
    const existingAttempt = verifyEmailActionCodeAttempts.get(oobCode);
    if (existingAttempt) {
        await existingAttempt.promise;
        return;
    }

    const attempt: IVerifyEmailActionCodeAttempt = {
        promise: Promise.resolve(),
        succeeded: false,
    };
    attempt.promise = applyActionCode(authInstance, oobCode).then(() => {
        attempt.succeeded = true;
    });
    verifyEmailActionCodeAttempts.set(oobCode, attempt);

    try {
        await attempt.promise;
    } catch (error) {
        if (!attempt.succeeded) {
            verifyEmailActionCodeAttempts.delete(oobCode);
        }
        throw error;
    }
};

function getSocialClaimPollIntervalMs(): number {
    const rawValue = Number(process.env.REACT_APP_SOCIAL_CLAIM_POLL_INTERVAL_MS || '');

    if (!Number.isFinite(rawValue) || rawValue < 25) {
        return DEFAULT_SOCIAL_CLAIM_POLL_INTERVAL_MS;
    }

    return rawValue;
}

interface Props {
    customCursorActive: boolean,
    hasSound: boolean,
    currentSessionId: string | null,
    setUserEmailVerified: React.Dispatch<React.SetStateAction<boolean>>,
    forceSignOutToAnonymous: () => Promise<boolean>,
    setInputFocused: React.Dispatch<React.SetStateAction<boolean>>,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}

function AuthActionView({
    customCursorActive,
    hasSound,
    currentSessionId,
    setUserEmailVerified,
    forceSignOutToAnonymous,
    setInputFocused,
    setSnackbarData,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element {
    // ===== General Constants =====

    const ACTION_BUTTON_WIDTH = 14.5 * BODY_FONT_SIZE;
    const ACTION_BUTTON_HEIGHT = 2.5 * BODY_FONT_SIZE;
    const INPUT_LABEL_FONT_SIZE = 0.6 * BODY_FONT_SIZE;
    const ACTION_BUTTON_FONT_SIZE = 1.2 * INPUT_LABEL_FONT_SIZE;
    const ACTION_BUTTON_TEXT_STYLE = (focused: boolean): {
        borderRadius: number,
        fontFamily: string,
        fontSize: number,
        fontColor: string,
    } => ({
        borderRadius: 0,
        fontFamily: FONT_TYPE.PRESS_START,
        fontSize: ACTION_BUTTON_FONT_SIZE,
        fontColor: focused ? theme.color.white : theme.verascopeColor.purple300,
    });
    const SNACKBAR_MESSAGE_RESET_EMAIL_SENT = (email: string): string => `An email has been sent to your email address, ${
        email
    }. Follow the directions in the email to reset your password.`;
    const SNACKBAR_MESSAGE_RESET_EMAIL_ACCOUNT_NOT_FOUND = 'We couldn\'t find an account with that email. Check for typos or create a new account.';
    const SNACKBAR_MESSAGE_RESET_EMAIL_WRONG_PROVIDER = (
        'This email is linked to a social sign-in account. '
        + 'Please sign in with Google, GitHub, Facebook, or X/Twitter.'
    );
    // eslint-disable-next-line max-len
    const SNACKBAR_MESSAGE_RESET_EMAIL_MISSING_EMAIL = `We found your account, but it's missing an email address. Please contact verascope support at ${SUPPORT_EMAIL_ADDRESS}.`;
    const SNACKBAR_MESSAGE_RESET_EMAIL_FALLBACK_ERROR = (errorMessage: string): string => (
        `Encountered error while resetting password: ${errorMessage}. Please reach out to verascope support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_NETWORK_ERROR = (
        'Your verification link was accepted, but we could not refresh your session right now. '
        + 'Please check your connection and try again.'
    );
    const SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_TOO_MANY_REQUESTS = (
        'Your verification link was accepted, but we\'re being rate limited by our cloud providers. Please wait a moment and try again.'
    );
    const SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_SIGN_IN_AGAIN = (
        'Your verification link was accepted, but your session expired. Please sign in again to continue.'
    );
    const SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_ACCOUNT_UNAVAILABLE = (
        'Your verification link was accepted, but this account is currently unavailable. '
        + `Please contact support at ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_FALLBACK_ERROR = (
        'Your verification link was accepted, but we could not refresh your account status right now. '
        + `Please try again or contact support at ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const STATUS_MESSAGE_UNSUBSCRIBE_SUCCESS_MAILING_LIST = (
        'You\'re all set. You will no longer receive mailing list emails from verascope.'
    );
    const STATUS_MESSAGE_UNSUBSCRIBE_SUCCESS_COMMENT_UPDATES = (
        'You\'re all set. You will no longer receive annotation comment update emails from verascope.'
    );
    const STATUS_MESSAGE_UNSUBSCRIBE_SUCCESS_CART_ABANDONMENT = (
        'You\'re all set. You will no longer receive cart abandonment reminder emails from verascope.'
    );
    const STATUS_MESSAGE_UNSUBSCRIBE_LINK_NOT_FOUND = (
        'This unsubscribe link is invalid or has already been removed. '
        + `Please contact the verascope team for support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const STATUS_MESSAGE_UNSUBSCRIBE_UNSUPPORTED = (
        'This email type does not support one-click unsubscribe. '
        + `Please contact the verascope team for support at: ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const STATUS_MESSAGE_UNSUBSCRIBE_ACCOUNT_UNAVAILABLE = (
        'We could not find the account linked to this unsubscribe request. '
        + `Please contact ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const STATUS_MESSAGE_UNSUBSCRIBE_GENERAL_ERROR = (
        'We hit a problem while processing your unsubscribe request. '
        + `Please try again or contact ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const STATUS_MESSAGE_BOOK_INVITE_CLAIM_REVOKED = (
        'Book invite no longer available. This book invite was revoked by the sender.'
    );
    const STATUS_MESSAGE_BOOK_INVITE_CLAIM_EXPIRED = (
        'Book invite link expired. Ask the sender to resend your book invite.'
    );
    const STATUS_MESSAGE_BOOK_INVITE_CLAIM_INVALID = (
        `This book invite link is invalid or no longer active. Please contact the sender or verascope support at ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const STATUS_MESSAGE_BOOK_INVITE_CLAIM_OWNER_READY = (
        'You\'re signed in as the sender. Claiming this invite will apply it to your own account.'
    );
    const STATUS_MESSAGE_BOOK_INVITE_CLAIM_OWNER_ALREADY_HAS_ACCESS = (
        'You already have access to the book, so this invite can\'t be claimed by your account. '
        + 'The recipient can still claim it, or you can revoke and resend it from your Book Invites page.'
    );
    const STATUS_MESSAGE_SOCIAL_CLAIM_INVALID = (
        `This discount claim link is invalid or no longer active. Please contact ${SUPPORT_EMAIL_ADDRESS}.`
    );
    const STATUS_MESSAGE_SOCIAL_CLAIM_EXPIRED = (
        'This discount claim link has expired. Return to the original post and request a fresh code.'
    );
    const STATUS_MESSAGE_SOCIAL_CLAIM_NO_CLAIMS = (
        'We verified your YouTube account, but there aren’t any active claims waiting for it right now. '
        + 'Try commenting again on the Short you came from, then return here.'
    );
    const normalizeSocialClaimPlatform = (
        platform?: string,
    ): SOCIAL_AUTOMATION_PLATFORM | null => {
        const normalizedPlatform = platform?.trim().toLowerCase() || '';

        switch (normalizedPlatform) {
        case SOCIAL_AUTOMATION_PLATFORM.youtube:
            return SOCIAL_AUTOMATION_PLATFORM.youtube;
        case SOCIAL_AUTOMATION_PLATFORM.instagram:
            return SOCIAL_AUTOMATION_PLATFORM.instagram;
        case SOCIAL_AUTOMATION_PLATFORM.threads:
            return SOCIAL_AUTOMATION_PLATFORM.threads;
        case SOCIAL_AUTOMATION_PLATFORM.x:
            return SOCIAL_AUTOMATION_PLATFORM.x;
        default:
            return null;
        }
    };
    const socialClaimDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
    });
    const formatSocialClaimPlatform = (platform?: string): string => {
        const normalizedPlatform = normalizeSocialClaimPlatform(platform);

        if (normalizedPlatform === SOCIAL_AUTOMATION_PLATFORM.youtube) return 'YouTube';
        if (normalizedPlatform === SOCIAL_AUTOMATION_PLATFORM.instagram) return 'Instagram';
        if (normalizedPlatform === SOCIAL_AUTOMATION_PLATFORM.threads) return 'Threads';
        if (normalizedPlatform === SOCIAL_AUTOMATION_PLATFORM.x) return 'X';

        return platform || '';
    };
    const formatSocialClaimDateTime = (value?: string): string => {
        if (!value) return '';

        const parsedDate = new Date(value);

        if (Number.isNaN(parsedDate.getTime())) {
            return '';
        }

        return socialClaimDateTimeFormatter.format(parsedDate);
    };
    const formatDiscountPercentOff = (value?: number | null): string => {
        if (value == null || !Number.isFinite(value)) {
            return '';
        }

        const normalizedValue = Number.isInteger(value)
            ? `${value}`
            : `${value}`.replace(/(\.\d*?[1-9])0+$/u, '$1').replace(/\.0+$/u, '');

        return `${normalizedValue}%`;
    };
    const buildDefaultClaimDiscountContinueUrl = (): string => {
        const continueSearchParams = new URLSearchParams();

        continueSearchParams.set(READER_PARAMS_TYPE.openBuyOptions, 'true');

        return `/${PAGE_ROUTE.book}?${continueSearchParams.toString()}`;
    };
    const getVerifiedSocialClaimMessage = (preview: ISocialAutomationClaimPreviewResult): string => {
        const discountPercentOff = formatDiscountPercentOff(preview.discountPercentOff);

        if (discountPercentOff) {
            return `You're verified. Use the code below for ${discountPercentOff} off a book before the claim expires.`;
        }

        return 'You\'re verified. Use the code below before the claim expires.';
    };
    const getSocialClaimReadyMessage = (preview: ISocialAutomationClaimPreviewResult): string => {
        const normalizedPlatform = normalizeSocialClaimPlatform(preview.platform);
        const platformLabel = formatSocialClaimPlatform(preview.platform) || 'social';
        const commenterLabel = preview.commenterUsername || 'the same commenter';

        if (
            normalizedPlatform === SOCIAL_AUTOMATION_PLATFORM.youtube
            && !preview.commenterUsername
        ) {
            return 'Verify with the same YouTube account you used to comment. We\'ll show every active code tied to that account.';
        }

        return (
            `Verify with the same ${platformLabel} account that commented as ${commenterLabel}. `
            + 'Once that matches, we\'ll reveal your code here.'
        );
    };
    /**
     * Keeps the carousel index inside the current claim array bounds even when
     * the page reloads with a shorter claim list after polling or expiry.
     */
    const normalizeCarouselIndex = (index: number, itemCount: number): number => {
        if (itemCount <= 0) {
            return 0;
        }

        return ((index % itemCount) + itemCount) % itemCount;
    };
    const resolveBookInviteClaimErrorMessage = (error: unknown): string => {
        if (!(error instanceof FirebaseError)) {
            return STATUS_MESSAGE_BOOK_INVITE_CLAIM_INVALID;
        }
        if (
            error.code === 'functions/not-found'
            || error.code === 'functions/invalid-argument'
        ) {
            return STATUS_MESSAGE_BOOK_INVITE_CLAIM_INVALID;
        }
        if (error.code === 'functions/failed-precondition') {
            const details = error as FirebaseError & {
                details?: {
                    reason?: string,
                },
            };
            if (details.details?.reason === BOOK_INVITE_CLAIM_CALLABLE_ERROR_REASON.revoked) {
                return STATUS_MESSAGE_BOOK_INVITE_CLAIM_REVOKED;
            }
            if (details.details?.reason === BOOK_INVITE_CLAIM_CALLABLE_ERROR_REASON.expired) {
                return STATUS_MESSAGE_BOOK_INVITE_CLAIM_EXPIRED;
            }
            if (
                details.details?.reason
                === BOOK_INVITE_CLAIM_CALLABLE_ERROR_REASON.ownerAlreadyHasBookAccess
            ) {
                return STATUS_MESSAGE_BOOK_INVITE_CLAIM_OWNER_ALREADY_HAS_ACCESS;
            }
        }
        return STATUS_MESSAGE_BOOK_INVITE_CLAIM_INVALID;
    };

    // ===== React Router =====

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // ===== State =====

    const [status, setStatus] = useState<AUTH_ACTION_STATUS_TYPE>(AUTH_ACTION_STATUS_TYPE.loading);
    const [statusHeader, setStatusHeader] = useState<string>('One moment');
    const [statusMessage, setStatusMessage] = useState<string>('We\'re validating your link.');
    const [linkedEmail, setLinkedEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
    const [passwordSchema] = useState<PasswordValidator>(
        new PasswordValidator()
            .is()
            .min(8)                                         // Minimum length 8
            .is()
            .max(100)                                       // Maximum length 100
            .has()
            .uppercase()                                    // Must have uppercase letters
            .has()
            .lowercase()                                    // Must have lowercase letters
            .has()
            .digits(2)                                      // Must have at least 2 digits
            .has()
            .not()
            .spaces()                                       // Should not have spaces
            .is()
            .not()
            .oneOf(TaiPasswordStrength.commonPasswords),    // Blacklist these values
    );
    const [passwordErrors, setPasswordErrors] = useState<IPasswordError[]>([]);
    const [readyErrorMessage, setReadyErrorMessage] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [requestingNewLink, setRequestingNewLink] = useState<boolean>(false);
    const [resetButtonFocused, setResetButtonFocused] = useState<boolean>(false);
    const [footerButtonFocused, setFooterButtonFocused] = useState<boolean>(false);
    const [claimFirstName, setClaimFirstName] = useState<string>('');
    const [claimLastName, setClaimLastName] = useState<string>('');
    const [claimSubmitting, setClaimSubmitting] = useState<boolean>(false);
    const [socialClaimPreview, setSocialClaimPreview] = useState<ISocialAutomationClaimPreviewResult | null>(null);
    const [socialClaimCarouselIndex, setSocialClaimCarouselIndex] = useState<number>(0);
    const successClip = useLazyAudioRef({
        src: Success,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const completedResetPasswordOobCodeRef = useRef<string | null>(null);
    const completedBookInviteClaimKeyRef = useRef<string | null>(null);
    const completedVerifyEmailOobCodeRef = useRef<string | null>(null);
    const completedRecoverEmailOobCodeRef = useRef<string | null>(null);
    const completedUnsubscribeKeyRef = useRef<string | null>(null);
    const previousStatusRef = useRef<AUTH_ACTION_STATUS_TYPE>(AUTH_ACTION_STATUS_TYPE.loading);
    const recordedViewPageUserActionKey = useRef<string | null>(null);

    const mode = searchParams.get(AUTH_ACTION_QUERY_PARAM.mode);
    const oobCode = searchParams.get(AUTH_ACTION_QUERY_PARAM.oobCode);
    const continueUrl = searchParams.get(AUTH_ACTION_QUERY_PARAM.continueUrl);
    const mailTrackingId = searchParams.get(AUTH_ACTION_QUERY_PARAM.mailTrackingId);
    const claimOwnerUserId = searchParams.get(AUTH_ACTION_QUERY_PARAM.ownerUserId);
    const claimAssignmentId = searchParams.get(AUTH_ACTION_QUERY_PARAM.assignmentId);
    const claimToken = searchParams.get(AUTH_ACTION_QUERY_PARAM.claimToken);
    const claimLookupToken = searchParams.get(AUTH_ACTION_QUERY_PARAM.claimLookupToken);
    const claimPlatform = searchParams.get(AUTH_ACTION_QUERY_PARAM.platform);
    const bookInviteClaimKey = `${claimOwnerUserId || ''}:${claimAssignmentId || ''}:${claimToken || ''}`;
    const claimFlashMessage = searchParams.get('claim_message');
    const claimFlashStatus = searchParams.get('claim_status');
    const authActionTestParam = searchParams.get(AUTH_ACTION_QUERY_PARAM.test);
    const authActionHasValidTestParam = (
        authActionTestParam === 'true'
        || authActionTestParam === 'false'
    );
    const authActionIsTest = authActionTestParam === 'true';
    const unsubscribeHasValidTestParam = authActionHasValidTestParam;
    const unsubscribeIsTest = authActionIsTest;
    const unsubscribeKey = `${mailTrackingId || ''}:${unsubscribeIsTest}`;
    const claimHasValidTestParam = authActionHasValidTestParam;
    const claimIsTest = authActionIsTest;

    const passwordValidationErrors = useMemo<IPasswordError[]>(() => {
        const validationResult = passwordSchema.validate(password, { details: true });
        if (typeof validationResult === 'boolean') return [];
        return [...validationResult] as IPasswordError[];
    }, [
        password,
        passwordSchema,
    ]);
    const passwordStrength = useMemo<PASSWORD_STRENGTH | null>(() => {
        if (!password) return null;
        const strengthTester = new TaiPasswordStrength.PasswordStrength();
        strengthTester.addCommonPasswords(TaiPasswordStrength.commonPasswords);
        strengthTester.addTrigraphMap(TaiPasswordStrength.trigraphs);
        const strengthTestResult = strengthTester.check(password);

        if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.veryWeak) >= 0) {
            return PASSWORD_STRENGTH.veryWeak;
        }
        if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.weak) >= 0) {
            return PASSWORD_STRENGTH.weak;
        }
        if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.reasonable) >= 0) {
            return PASSWORD_STRENGTH.reasonable;
        }
        if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.strong) >= 0) {
            return PASSWORD_STRENGTH.strong;
        }
        if (strengthTestResult.strengthCode.indexOf(PASSWORD_STRENGTH.veryStrong) >= 0) {
            return PASSWORD_STRENGTH.veryStrong;
        }

        return null;
    }, [password]);
    const passwordPassesChecks = password.length > 0 && passwordValidationErrors.length === 0;
    const passwordsMatch = password.length > 0 && passwordConfirmation.length > 0 && password === passwordConfirmation;
    const passwordInputInvalid = password.length > 0 && !passwordPassesChecks;
    const passwordConfirmationInputInvalid = passwordConfirmation.length > 0 && !passwordsMatch;
    const resetPasswordSubmitDisabled = (
        submitting
        || status !== AUTH_ACTION_STATUS_TYPE.ready
        || !passwordPassesChecks
        || !passwordsMatch
    );
    const claimBookInviteIsOwnerSelfClaim = (
        mode === AUTH_ACTION_MODE_TYPE.claimBookInvite
        && !!auth.currentUser?.uid
        && auth.currentUser.uid === claimOwnerUserId
    );
    const claimBookInviteSubmitDisabled = (
        claimSubmitting
        || status !== AUTH_ACTION_STATUS_TYPE.ready
        || !claimOwnerUserId
        || !claimAssignmentId
        || !claimToken
        || (
            !claimBookInviteIsOwnerSelfClaim
            && (
                claimFirstName.trim().length === 0
                || claimLastName.trim().length === 0
            )
        )
    );
    const claimBookInviteButtonFocused = !claimBookInviteSubmitDisabled && resetButtonFocused;
    const claimDiscountSubmitDisabled = (
        claimSubmitting
        || status !== AUTH_ACTION_STATUS_TYPE.ready
        || !socialClaimPreview?.verificationUrl
    );
    const claimDiscountButtonFocused = !claimDiscountSubmitDisabled && resetButtonFocused;
    const resetPasswordButtonFocused = !resetPasswordSubmitDisabled && resetButtonFocused;
    const showResetPasswordSubmitButton = (
        submitting
        || (passwordPassesChecks && passwordsMatch)
    );

    // ===== Methods =====

    const recordAuthActionError = (error: unknown): void => {
        if (currentSessionId) {
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.error,
                sessionId: currentSessionId,
                payload: {
                    error,
                },
            });
        }
    };
    /**
     * Mirrors Firebase Auth's emailVerified value into the Firestore user item
     * so every open tab can refresh through the existing user ping listener.
     */
    const syncVerifiedEmailStatusToUserItem = async (): Promise<boolean> => {
        if (!auth.currentUser || auth.currentUser.isAnonymous) return false;

        try {
            const emailVerified = await syncEmailVerificationStatus({
                test: isTestMode(),
            });
            if (emailVerified) {
                setUserEmailVerified(true);
            }
            return emailVerified;
        } catch (error: unknown) {
            recordAuthActionError(error);
            return false;
        }
    };

    /**
     * Confirms whether a consumed verification code already landed for the
     * current signed-in user before showing an invalid-link error.
     */
    const hasVerifiedCurrentUserEmail = async (): Promise<boolean> => {
        if (!auth.currentUser || auth.currentUser.isAnonymous) return false;

        try {
            await auth.currentUser.reload();
            const emailVerified = !!auth.currentUser.emailVerified;
            setUserEmailVerified(emailVerified);
            if (emailVerified) {
                syncVerifiedEmailStatusToUserItem();
            }
            return emailVerified;
        } catch (error: unknown) {
            recordAuthActionError(error);
            return false;
        }
    };

    const showVerifyEmailSuccess = (): void => {
        setStatus(AUTH_ACTION_STATUS_TYPE.success);
        setStatusHeader('Email verification successful');
        setStatusMessage('Your email address has been verified. You can continue using your account.');
    };

    const getVerifyEmailReloadFailureMessage = (error: unknown): string => {
        if (!(error instanceof FirebaseError)) {
            return SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_FALLBACK_ERROR;
        }

        switch (error.code as AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE) {
        case AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.networkRequestFailed:
            return SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_NETWORK_ERROR;
        case AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.tooManyRequests:
            return SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_TOO_MANY_REQUESTS;
        case AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.userTokenExpired:
        case AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.invalidUserToken:
            return SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_SIGN_IN_AGAIN;
        case AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.userDisabled:
        case AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.userNotFound:
            return SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_ACCOUNT_UNAVAILABLE;
        default:
            return SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_FALLBACK_ERROR;
        }
    };

    const shouldSignOutAfterVerifyEmailReloadError = (error: unknown): boolean => (
        error instanceof FirebaseError
        && (
            error.code === AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.userTokenExpired
            || error.code === AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.invalidUserToken
            || error.code === AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.userDisabled
            || error.code === AUTH_ACTION_VERIFY_RELOAD_ERROR_CODE.userNotFound
        )
    );

    const onLogoEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.logomark,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onLogoLeave = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorLeave(e);
    };

    const onResetButtonEnter = (e: React.MouseEvent): void => {
        setResetButtonFocused(true);
        onCursorEnter(
            CURSOR_TARGET.resetPasswordButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onResetButtonLeave = (e: React.MouseEvent): void => {
        setResetButtonFocused(false);
        onCursorLeave(e);
    };

    const onFooterButtonEnter = (e: React.MouseEvent): void => {
        setFooterButtonFocused(true);
        onCursorEnter(
            CURSOR_TARGET.resetPasswordButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onFooterButtonLeave = (e: React.MouseEvent): void => {
        setFooterButtonFocused(false);
        onCursorLeave(e);
    };

    const onClaimCarouselControlEnter = (e: React.MouseEvent | React.TouchEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.claimCarouselButton,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    const onClaimCarouselControlLeave = (
        e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent,
    ): void => {
        onCursorLeave(e);
    };

    const onLogoClick = (e: React.MouseEvent): void => {
        onCursorLeave(e);
        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    const submitResetPassword = async (): Promise<void> => {
        if (
            !oobCode
            || status !== AUTH_ACTION_STATUS_TYPE.ready
            || resetPasswordSubmitDisabled
        ) {
            return;
        }

        if (passwordValidationErrors.length > 0) {
            setPasswordErrors(passwordValidationErrors);
            setReadyErrorMessage(passwordValidationErrors[0].message);
            return;
        }
        if (passwordErrors.length > 0) setPasswordErrors([]);

        if (!passwordsMatch) {
            setReadyErrorMessage('Your password entries do not match. Please try again.');
            return;
        }

        setReadyErrorMessage('');
        setSubmitting(true);

        try {
            await confirmPasswordReset(auth, oobCode, password);
            completedResetPasswordOobCodeRef.current = oobCode;
            let signedInAfterReset = false;
            if (linkedEmail) {
                try {
                    await signInWithEmailAndPassword(auth, linkedEmail, password);
                    signedInAfterReset = true;
                } catch (error: unknown) {
                    recordAuthActionError(error);
                    signedInAfterReset = false;
                }
            }
            setReadyErrorMessage('');
            setStatus(AUTH_ACTION_STATUS_TYPE.success);
            setStatusHeader('Password reset successful');
            setStatusMessage(
                signedInAfterReset
                    ? 'Your password has been updated and you are now signed in.'
                    : 'Your password has been updated. Please sign in with your new password.',
            );
        } catch (error: unknown) {
            recordAuthActionError(error);
            let message = `This reset link is no longer valid. Please request a new reset email or contact ${SUPPORT_EMAIL_ADDRESS}.`;
            if (
                error instanceof FirebaseError
                && error.code === 'auth/weak-password'
            ) {
                message = 'Your new password is too weak. Please choose a stronger password and try again.';
                setReadyErrorMessage(message);
                return;
            }
            setStatus(AUTH_ACTION_STATUS_TYPE.error);
            setStatusHeader('Reset unsuccessful');
            setStatusMessage(message);
        } finally {
            setSubmitting(false);
        }
    };

    const submitClaimBookInvite = async (): Promise<void> => {
        if (
            claimBookInviteSubmitDisabled
            || !claimOwnerUserId
            || !claimAssignmentId
            || !claimToken
        ) {
            return;
        }
        setClaimSubmitting(true);
        try {
            const claimResult = await claimBookInvite({
                test: claimIsTest,
                ownerUserId: claimOwnerUserId,
                assignmentId: claimAssignmentId,
                claimToken,
                firstName: (
                    !claimBookInviteIsOwnerSelfClaim && claimFirstName.trim().length > 0
                )
                    ? claimFirstName.trim()
                    : undefined,
                lastName: (
                    !claimBookInviteIsOwnerSelfClaim && claimLastName.trim().length > 0
                )
                    ? claimLastName.trim()
                    : undefined,
            });
            completedBookInviteClaimKeyRef.current = bookInviteClaimKey;
            if (claimResult.customToken) {
                try {
                    await signInWithCustomToken(auth, claimResult.customToken);
                } catch (error: unknown) {
                    recordAuthActionError(error);
                }
            }
            setReadyErrorMessage('');
            setStatus(AUTH_ACTION_STATUS_TYPE.success);
            setStatusHeader('Book invite claimed!');
            setStatusMessage('You\'re all set. Your book invite is claimed, and the book is now available to read.');
        } catch (error: unknown) {
            recordAuthActionError(error);
            const message = resolveBookInviteClaimErrorMessage(error);
            setStatusMessage(message);
            setStatus(AUTH_ACTION_STATUS_TYPE.error);
            setStatusHeader('Claim unavailable');
        } finally {
            setClaimSubmitting(false);
        }
    };

    const submitClaimDiscountVerification = (): void => {
        if (claimDiscountSubmitDisabled || !socialClaimPreview?.verificationUrl) {
            return;
        }

        setClaimSubmitting(true);
        window.open(socialClaimPreview.verificationUrl, '_self');
    };

    const proceedFromSuccess = (): void => {
        const navigateToContinueUrl = (): boolean => {
            const effectiveContinueUrl = (
                mode === AUTH_ACTION_MODE_TYPE.claimDiscount
                    ? (continueUrl || buildDefaultClaimDiscountContinueUrl())
                    : continueUrl
            );

            if (!effectiveContinueUrl) return false;

            try {
                const parsedContinueUrl = new URL(effectiveContinueUrl, window.location.origin);
                const isSameOrigin = parsedContinueUrl.origin === window.location.origin;
                if (isSameOrigin) {
                    navigate(
                        `${parsedContinueUrl.pathname}${parsedContinueUrl.search}${parsedContinueUrl.hash}`,
                        {
                            state: {
                                prevPath: location.pathname,
                            },
                        },
                    );
                    return true;
                }
            } catch {
                // Fall back to location.assign for malformed or non-standard continue URLs.
            }

            window.location.assign(effectiveContinueUrl);
            return true;
        };

        if (mode === AUTH_ACTION_MODE_TYPE.claimBookInvite) {
            navigate(
                `/${PAGE_ROUTE.book}`,
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
            return;
        }
        if (mode === AUTH_ACTION_MODE_TYPE.openProfile) {
            if (navigateToContinueUrl()) return;
            navigate(
                '/',
                {
                    state: {
                        prevPath: location.pathname,
                    },
                },
            );
            return;
        }
        if (navigateToContinueUrl()) return;

        navigate(
            `/${PAGE_ROUTE.landing}`,
            {
                state: {
                    prevPath: location.pathname,
                },
            },
        );
    };

    const requestNewResetLink = async (): Promise<void> => {
        if (requestingNewLink) return;

        const resetEmail = linkedEmail;
        if (!resetEmail) {
            setStatus(AUTH_ACTION_STATUS_TYPE.error);
            setStatusHeader('Unable to request new link');
            setStatusMessage(
                // eslint-disable-next-line max-len
                `We could not find an email address for this reset request. Please try forgot password again or contact ${SUPPORT_EMAIL_ADDRESS}.`,
            );
            return;
        }

        setRequestingNewLink(true);
        try {
            await sendResetPasswordEmail({
                test: isTestMode(),
                email: resetEmail,
            });
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: SNACKBAR_MESSAGE_RESET_EMAIL_SENT(resetEmail),
                icon: EnvelopeIcon,
                hasSuccess: true,
            });
            setStatus(AUTH_ACTION_STATUS_TYPE.success);
            setStatusHeader('New reset email sent');
            setStatusMessage(`We sent a fresh password reset email to ${resetEmail}.`);
        } catch (error: unknown) {
            recordAuthActionError(error);
            let message = SNACKBAR_MESSAGE_RESET_EMAIL_FALLBACK_ERROR('Unknown error');
            if (error instanceof FirebaseError) {
                if (error.code === RESET_PASSWORD_CALLABLE_ERROR_CODE.notFound) {
                    message = SNACKBAR_MESSAGE_RESET_EMAIL_ACCOUNT_NOT_FOUND;
                } else if (error.code === RESET_PASSWORD_CALLABLE_ERROR_CODE.failedPrecondition) {
                    const details = error as FirebaseError & {
                        details?: {
                            reason?: string,
                        },
                    };
                    if (details.details?.reason === RESET_PASSWORD_CALLABLE_ERROR_REASON.wrongAuthenticationType) {
                        message = SNACKBAR_MESSAGE_RESET_EMAIL_WRONG_PROVIDER;
                    } else if (details.details?.reason === RESET_PASSWORD_CALLABLE_ERROR_REASON.missingEmail) {
                        message = SNACKBAR_MESSAGE_RESET_EMAIL_MISSING_EMAIL;
                    }
                } else {
                    message = getAuthErrorMessage(error.code as AUTHENTICATION_ERROR_CODE)
                        || SNACKBAR_MESSAGE_RESET_EMAIL_FALLBACK_ERROR(error.message);
                }
            }
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: message,
                icon: CautionIcon,
                hasFailure: true,
            });
            setStatus(AUTH_ACTION_STATUS_TYPE.error);
            setStatusHeader('Reset email failed');
            setStatusMessage(message);
        } finally {
            setRequestingNewLink(false);
        }
    };

    const requestNewVerifyLink = async (): Promise<void> => {
        if (requestingNewLink) return;

        setRequestingNewLink(true);
        try {
            const verifyEmailResult = await sendVerifyAccountEmail({
                test: isTestMode(),
            });
            if (!verifyEmailResult.success) {
                throw new Error('Verification email request failed.');
            }
            const wasAlreadySentInNewAccountEmail = (
                verifyEmailResult.status === SEND_VERIFY_ACCOUNT_EMAIL_STATUS.recentlySentInNewAccountEmail
            );
            const verifyEmailStatusMessage = wasAlreadySentInNewAccountEmail
                ? SNACKBAR_MESSAGE_VERIFY_EMAIL_RECENT_NEW_ACCOUNT
                : 'We sent a fresh verification email. Please open the latest email to verify your account.';
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: wasAlreadySentInNewAccountEmail
                    ? SNACKBAR_MESSAGE_VERIFY_EMAIL_RECENT_NEW_ACCOUNT
                    : SNACKBAR_MESSAGE_VERIFY_EMAIL_SUCCESS,
                icon: EnvelopeIcon,
                hasSuccess: true,
            });
            setStatus(AUTH_ACTION_STATUS_TYPE.success);
            setStatusHeader(wasAlreadySentInNewAccountEmail
                ? 'Verification email already sent'
                : 'New verification email sent');
            setStatusMessage(verifyEmailStatusMessage);
        } catch (error: unknown) {
            recordAuthActionError(error);
            const message = getVerifyEmailRequestErrorMessage(error);
            setSnackbarData({
                visible: true,
                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                text: message,
                icon: CautionIcon,
                hasFailure: true,
            });
            setStatus(AUTH_ACTION_STATUS_TYPE.error);
            setStatusHeader('Verification email failed');
            setStatusMessage(message);
        } finally {
            setRequestingNewLink(false);
        }
    };

    /**
     * Records user action of viewing terms page
     */
    const recordAuthActionPageUserAction = async (): Promise<void> => {
        if (currentSessionId) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.viewAuthActionPage,
                sessionId: currentSessionId,
                payload: {
                    mode: mode || null,
                },
            });
        }
    };

    // ===== Side Effects =====

    useEffect(() => {
        const enteredSuccess = (
            previousStatusRef.current !== AUTH_ACTION_STATUS_TYPE.success
            && status === AUTH_ACTION_STATUS_TYPE.success
        );

        if (enteredSuccess && hasSound && successClip.current) {
            successClip.current.pause();
            successClip.current.currentTime = 0;
            playAudio(successClip.current);
        }

        previousStatusRef.current = status;
    }, [hasSound, status]);

    /**
     * Set Page Title
     */
    useEffect(() => {
        updatePageTitle(
            'Account Action',
        );
    }, []);

    /**
     * Record user action of viewing auth action page.
     */
    useEffect(() => {
        if (
            !currentSessionId
            || recordedViewPageUserActionKey.current === location.key
        ) {
            return;
        }
        recordAuthActionPageUserAction();
        recordedViewPageUserActionKey.current = location.key;
    }, [
        currentSessionId,
        location.key,
        mode,
    ]);

    /**
     * Validate and execute incoming firebase auth action link.
     */
    useEffect(() => {
        let active = true;

        const initializeAction = async (): Promise<void> => {
            if (completedResetPasswordOobCodeRef.current) {
                return;
            }
            if (
                completedBookInviteClaimKeyRef.current
                && completedBookInviteClaimKeyRef.current === bookInviteClaimKey
            ) {
                return;
            }
            if (
                completedVerifyEmailOobCodeRef.current
                && completedVerifyEmailOobCodeRef.current === oobCode
            ) {
                return;
            }
            if (
                completedRecoverEmailOobCodeRef.current
                && completedRecoverEmailOobCodeRef.current === oobCode
            ) {
                return;
            }
            if (
                completedUnsubscribeKeyRef.current
                && completedUnsubscribeKeyRef.current === unsubscribeKey
            ) {
                return;
            }

            if (!mode) {
                if (!active) return;
                setStatus(AUTH_ACTION_STATUS_TYPE.error);
                setStatusHeader('Invalid link');
                setStatusMessage(`This link is missing required information. Please request a new email or contact ${SUPPORT_EMAIL_ADDRESS}.`);
                return;
            }

            if (
                mode === AUTH_ACTION_MODE_TYPE.resetPassword
                || mode === AUTH_ACTION_MODE_TYPE.legacyResetPassword
            ) {
                if (!oobCode) {
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Invalid link');
                    setStatusMessage(
                        `This reset link is missing required information. Please request a new reset email or contact ${SUPPORT_EMAIL_ADDRESS}.`,
                    );
                    return;
                }
                try {
                    const email = await verifyPasswordResetCode(auth, oobCode);
                    if (!active) return;
                    setLinkedEmail(email);
                    setReadyErrorMessage('');
                    setStatus(AUTH_ACTION_STATUS_TYPE.ready);
                    setStatusHeader('Choose a new password');
                    setStatusMessage('Enter your new password below to finish resetting your account.');
                } catch (error: unknown) {
                    recordAuthActionError(error);
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Reset link expired');
                    setStatusMessage(
                        // eslint-disable-next-line max-len
                        `This password reset link is invalid or expired. Please request a new reset email or contact ${SUPPORT_EMAIL_ADDRESS}.`,
                    );
                }
                return;
            }

            if (mode === AUTH_ACTION_MODE_TYPE.verifyEmail) {
                if (!oobCode) {
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Invalid link');
                    setStatusMessage(
                        // eslint-disable-next-line max-len
                        `This verification link is missing required information. Please request a new verification email or contact ${SUPPORT_EMAIL_ADDRESS}.`,
                    );
                    return;
                }
                const refreshVerifiedCurrentUserAfterAcceptedVerifyEmail = async (): Promise<void> => {
                    if (!auth.currentUser || auth.currentUser.isAnonymous) return;

                    let timeoutId: ReturnType<typeof setTimeout> | null = null;
                    let timedOut = false;
                    const timeout = new Promise<void>((resolve) => {
                        timeoutId = setTimeout(() => {
                            timedOut = true;
                            resolve();
                        }, VERIFY_EMAIL_SESSION_REFRESH_TIMEOUT_MS);
                    });
                    const reloadCurrentUser = auth.currentUser.reload();

                    try {
                        await Promise.race([
                            reloadCurrentUser,
                            timeout,
                        ]);
                        if (timeoutId) clearTimeout(timeoutId);
                        if (!active) return;
                        if (timedOut) {
                            setSnackbarData({
                                visible: true,
                                duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                                text: SNACKBAR_MESSAGE_VERIFY_EMAIL_RELOAD_FALLBACK_ERROR,
                                icon: CautionIcon,
                                hasFailure: true,
                            });
                            return;
                        }
                        setUserEmailVerified(!!auth.currentUser.emailVerified);
                    } catch (error: unknown) {
                        if (timeoutId) clearTimeout(timeoutId);
                        recordAuthActionError(error);
                        if (!active) return;
                        setSnackbarData({
                            visible: true,
                            duration: DEFAULT_SNACKBAR_VISIBLE_DURATION,
                            text: getVerifyEmailReloadFailureMessage(error),
                            icon: CautionIcon,
                            hasFailure: true,
                        });
                        if (shouldSignOutAfterVerifyEmailReloadError(error)) {
                            try {
                                await forceSignOutToAnonymous();
                            } catch (signOutError: unknown) {
                                recordAuthActionError(signOutError);
                            }
                        }
                    }
                };

                try {
                    await applyVerifyEmailActionCodeOnce({
                        authInstance: auth,
                        oobCode,
                    });
                    completedVerifyEmailOobCodeRef.current = oobCode;
                    if (!active) return;
                    showVerifyEmailSuccess();
                    syncVerifiedEmailStatusToUserItem();
                    refreshVerifiedCurrentUserAfterAcceptedVerifyEmail();
                } catch (error: unknown) {
                    recordAuthActionError(error);
                    const verificationAlreadyLanded = await hasVerifiedCurrentUserEmail();
                    if (!active) return;
                    if (verificationAlreadyLanded) {
                        completedVerifyEmailOobCodeRef.current = oobCode;
                        showVerifyEmailSuccess();
                        return;
                    }
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Verification unsuccessful');
                    setStatusMessage(
                        // eslint-disable-next-line max-len
                        `This verification link is invalid or expired. Request a new verification email or contact ${SUPPORT_EMAIL_ADDRESS}.`,
                    );
                }
                return;
            }

            if (mode === AUTH_ACTION_MODE_TYPE.claimBookInvite) {
                if (
                    !claimOwnerUserId
                    || !claimAssignmentId
                    || !claimToken
                    || !claimHasValidTestParam
                ) {
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Invalid link');
                    setStatusMessage(STATUS_MESSAGE_BOOK_INVITE_CLAIM_INVALID);
                    return;
                }
                try {
                    const claimPreview = await getBookInviteClaimPreview({
                        test: claimIsTest,
                        ownerUserId: claimOwnerUserId,
                        assignmentId: claimAssignmentId,
                        claimToken,
                    });
                    if (!active) return;
                    if (claimPreview.status === BOOK_INVITE_CLAIM_PREVIEW_STATUS.notFound) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.error);
                        setStatusHeader('Invalid link');
                        setStatusMessage(STATUS_MESSAGE_BOOK_INVITE_CLAIM_INVALID);
                        return;
                    }
                    if (claimPreview.status === BOOK_INVITE_CLAIM_PREVIEW_STATUS.revoked) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.error);
                        setStatusHeader('Book invite unavailable');
                        setStatusMessage(STATUS_MESSAGE_BOOK_INVITE_CLAIM_REVOKED);
                        return;
                    }
                    if (claimPreview.status === BOOK_INVITE_CLAIM_PREVIEW_STATUS.expired) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.error);
                        setStatusHeader('Book invite link expired');
                        setStatusMessage(STATUS_MESSAGE_BOOK_INVITE_CLAIM_EXPIRED);
                        return;
                    }
                    if (claimPreview.status === BOOK_INVITE_CLAIM_PREVIEW_STATUS.claimed) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.success);
                        setStatusHeader('Already claimed');
                        setStatusMessage('You\'re all set. Your book invite is claimed, and the book is now available to read.');
                        return;
                    }
                    if (auth.currentUser?.uid && auth.currentUser.uid === claimOwnerUserId) {
                        setLinkedEmail('');
                        setClaimFirstName('');
                        setClaimLastName('');
                        setStatus(AUTH_ACTION_STATUS_TYPE.ready);
                        setStatusHeader('Claim this invite to your account');
                        setStatusMessage(STATUS_MESSAGE_BOOK_INVITE_CLAIM_OWNER_READY);
                        return;
                    }
                    setLinkedEmail(claimPreview.recipientEmail || '');
                    setClaimFirstName(claimPreview.recipientFirstName || '');
                    setClaimLastName(claimPreview.recipientLastName || '');
                    setStatus(AUTH_ACTION_STATUS_TYPE.ready);
                    setStatusHeader('Claim your book invite');
                    setStatusMessage('Confirm your name and claim this book invite.');
                } catch (error: unknown) {
                    recordAuthActionError(error);
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Claim unavailable');
                    setStatusMessage(resolveBookInviteClaimErrorMessage(error));
                }
                return;
            }
            if (mode === AUTH_ACTION_MODE_TYPE.claimDiscount) {
                if (!claimToken && !claimPlatform) {
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Invalid link');
                    setStatusMessage(STATUS_MESSAGE_SOCIAL_CLAIM_INVALID);
                    return;
                }
                try {
                    const claimPreview = await getSocialAutomationClaimPreview({
                        claimLookupToken: claimLookupToken || undefined,
                        claimToken: claimToken || undefined,
                        platform: claimPlatform || undefined,
                    });
                    if (!active) return;

                    setSocialClaimPreview(claimPreview);

                    if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.notFound) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.error);
                        setStatusHeader('Claim unavailable');
                        setStatusMessage(STATUS_MESSAGE_SOCIAL_CLAIM_INVALID);
                        return;
                    }
                    if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.expired) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.error);
                        setStatusHeader('Claim expired');
                        setStatusMessage(STATUS_MESSAGE_SOCIAL_CLAIM_EXPIRED);
                        return;
                    }
                    if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.noClaims) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.error);
                        setStatusHeader('No active claims found');
                        setStatusMessage(STATUS_MESSAGE_SOCIAL_CLAIM_NO_CLAIMS);
                        return;
                    }
                    if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.eligibleClaims) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.success);
                        setStatusHeader('Your codes are ready');
                        setStatusMessage('We verified your YouTube account. Use any active code below before it expires.');
                        return;
                    }
                    if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.verified) {
                        setStatus(AUTH_ACTION_STATUS_TYPE.success);
                        setStatusHeader('Your code is ready');
                        setStatusMessage(getVerifiedSocialClaimMessage(claimPreview));
                        return;
                    }

                    setReadyErrorMessage(
                        claimFlashStatus === 'error'
                            ? (
                                claimFlashMessage
                                || claimPreview.lastVerificationError
                                || ''
                            )
                            : '',
                    );
                    setStatus(AUTH_ACTION_STATUS_TYPE.ready);
                    setStatusHeader('Verify your comment account');
                    setStatusMessage(getSocialClaimReadyMessage(claimPreview));
                } catch (error: unknown) {
                    recordAuthActionError(error);
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Claim unavailable');
                    setStatusMessage(STATUS_MESSAGE_SOCIAL_CLAIM_INVALID);
                }
                return;
            }
            if (mode === AUTH_ACTION_MODE_TYPE.openProfile) {
                if (!active) return;
                proceedFromSuccess();
                return;
            }

            if (mode === AUTH_ACTION_MODE_TYPE.recoverEmail) {
                if (!oobCode) {
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Invalid link');
                    setStatusMessage(
                        // eslint-disable-next-line max-len
                        `This recovery link is missing required information. Please request a new recovery email or contact ${SUPPORT_EMAIL_ADDRESS}.`,
                    );
                    return;
                }
                try {
                    await applyActionCode(auth, oobCode);
                    completedRecoverEmailOobCodeRef.current = oobCode;
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.success);
                    setStatusHeader('Email restored');
                    setStatusMessage('Your account email has been restored successfully.');
                } catch (error: unknown) {
                    recordAuthActionError(error);
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Email recovery unsuccessful');
                    setStatusMessage(`This recovery link is invalid or expired. Request a new recovery email or contact ${SUPPORT_EMAIL_ADDRESS}.`);
                }
                return;
            }

            if (mode === AUTH_ACTION_MODE_TYPE.unsubscribe) {
                if (!mailTrackingId || !unsubscribeHasValidTestParam) {
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Invalid link');
                    setStatusMessage(`This unsubscribe link is missing required information. Please contact ${SUPPORT_EMAIL_ADDRESS}.`);
                    return;
                }
                try {
                    const result = await unsubscribeByMailTrackingLink({
                        test: unsubscribeIsTest,
                        mailTrackingId,
                    });
                    completedUnsubscribeKeyRef.current = unsubscribeKey;
                    if (!active) return;
                    setStatus(AUTH_ACTION_STATUS_TYPE.success);
                    setStatusHeader('Unsubscribe successful');
                    if (result.type === UNSUBSCRIBE_TYPE.commentUpdates) {
                        setStatusMessage(STATUS_MESSAGE_UNSUBSCRIBE_SUCCESS_COMMENT_UPDATES);
                    } else if (result.type === UNSUBSCRIBE_TYPE.cartAbandonment) {
                        setStatusMessage(STATUS_MESSAGE_UNSUBSCRIBE_SUCCESS_CART_ABANDONMENT);
                    } else {
                        setStatusMessage(STATUS_MESSAGE_UNSUBSCRIBE_SUCCESS_MAILING_LIST);
                    }
                } catch (error: unknown) {
                    recordAuthActionError(error);
                    if (!active) return;
                    let message = STATUS_MESSAGE_UNSUBSCRIBE_GENERAL_ERROR;
                    if (error instanceof FirebaseError) {
                        const lowerCaseMessage = error.message.toLowerCase();
                        const normalizedErrorCode = error.code.replace(
                            'functions/',
                            '',
                        ) as UNSUBSCRIBE_BY_MAIL_TRACKING_LINK_ERROR_CODE;
                        switch (normalizedErrorCode) {
                        case UNSUBSCRIBE_BY_MAIL_TRACKING_LINK_ERROR_CODE.notFound:
                            message = lowerCaseMessage.includes('associated user account not found')
                                ? STATUS_MESSAGE_UNSUBSCRIBE_ACCOUNT_UNAVAILABLE
                                : STATUS_MESSAGE_UNSUBSCRIBE_LINK_NOT_FOUND;
                            break;
                        case UNSUBSCRIBE_BY_MAIL_TRACKING_LINK_ERROR_CODE.failedPrecondition:
                            message = lowerCaseMessage.includes('missing account details')
                                ? STATUS_MESSAGE_UNSUBSCRIBE_ACCOUNT_UNAVAILABLE
                                : STATUS_MESSAGE_UNSUBSCRIBE_UNSUPPORTED;
                            break;
                        case UNSUBSCRIBE_BY_MAIL_TRACKING_LINK_ERROR_CODE.invalidArgument:
                            message = STATUS_MESSAGE_UNSUBSCRIBE_LINK_NOT_FOUND;
                            break;
                        default:
                            message = STATUS_MESSAGE_UNSUBSCRIBE_GENERAL_ERROR;
                            break;
                        }
                    }
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('Unsubscribe unsuccessful');
                    setStatusMessage(message);
                }
                return;
            }

            if (!active) return;
            setStatus(AUTH_ACTION_STATUS_TYPE.error);
            setStatusHeader('Unsupported action');
            setStatusMessage(`This account action is not supported here. Please request a new email or contact ${SUPPORT_EMAIL_ADDRESS}.`);
        };

        initializeAction();

        return () => {
            active = false;
        };
    }, [
        currentSessionId,
        forceSignOutToAnonymous,
        claimAssignmentId,
        claimFlashMessage,
        claimFlashStatus,
        claimLookupToken,
        claimOwnerUserId,
        claimPlatform,
        claimToken,
        mailTrackingId,
        mode,
        oobCode,
        continueUrl,
        setUserEmailVerified,
        claimHasValidTestParam,
        claimIsTest,
        unsubscribeHasValidTestParam,
        unsubscribeIsTest,
    ]);

    const modeIsResetPassword = (
        mode === AUTH_ACTION_MODE_TYPE.resetPassword
        || mode === AUTH_ACTION_MODE_TYPE.legacyResetPassword
    );
    const modeIsClaimBookInvite = mode === AUTH_ACTION_MODE_TYPE.claimBookInvite;
    const modeIsClaimDiscount = mode === AUTH_ACTION_MODE_TYPE.claimDiscount;
    const socialClaimNeedsPolling = (
        modeIsClaimDiscount
        && !!claimLookupToken
        && (
            socialClaimPreview?.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.noClaims
            || socialClaimPreview?.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.pending
        )
    );
    const footerSupportsRequestNewLink = (
        modeIsResetPassword
        || mode === AUTH_ACTION_MODE_TYPE.verifyEmail
    );
    const authActionPageCopy = (() => {
        if (modeIsClaimDiscount) {
            return {
                title: 'CLAIM DISCOUNT',
                subtitle: 'Verify your social comment',
            };
        }
        if (modeIsClaimBookInvite) {
            return {
                title: 'BOOK INVITE',
                subtitle: 'Claim your book invite',
            };
        }
        if (modeIsResetPassword) {
            return {
                title: 'RESET PASSWORD',
                subtitle: 'Create a new password',
            };
        }
        if (mode === AUTH_ACTION_MODE_TYPE.verifyEmail) {
            return {
                title: 'VERIFY EMAIL',
                subtitle: 'Confirm your email address',
            };
        }
        if (mode === AUTH_ACTION_MODE_TYPE.unsubscribe) {
            return {
                title: 'EMAIL PREFERENCES',
                subtitle: 'Manage your email notifications',
            };
        }
        if (mode === AUTH_ACTION_MODE_TYPE.openProfile) {
            return {
                title: 'ACCOUNT',
                subtitle: 'Open your profile',
            };
        }
        return {
            title: 'ACCOUNT ACTION',
            subtitle: 'Secure and verify your account',
        };
    })();
    const footerActionButtonDisabled = footerSupportsRequestNewLink && requestingNewLink;
    const footerActionButtonFocused = !footerActionButtonDisabled && footerButtonFocused;

    const footerActionButtonText = (() => {
        if (status === AUTH_ACTION_STATUS_TYPE.success) {
            if (modeIsClaimBookInvite) {
                return 'READ BOOK';
            }
            if (modeIsClaimDiscount) {
                return 'VIEW BOOK';
            }
            return continueUrl ? 'CONTINUE' : 'GO HOME';
        }
        if (footerSupportsRequestNewLink && requestingNewLink) {
            return 'REQUESTING...';
        }
        return footerSupportsRequestNewLink ? 'REQUEST NEW LINK' : 'GO HOME';
    })();

    const hasStatusFailure = (
        status === AUTH_ACTION_STATUS_TYPE.error
        || (
            status === AUTH_ACTION_STATUS_TYPE.ready
            && readyErrorMessage.length > 0
        )
    );
    const socialClaimExpiry = (
        formatSocialClaimDateTime(socialClaimPreview?.expiresAt)
    );
    const socialClaimList = socialClaimPreview?.claims || [];
    const normalizedSocialClaimCarouselIndex = normalizeCarouselIndex(
        socialClaimCarouselIndex,
        socialClaimList.length,
    );
    const activeSocialClaim = socialClaimList[normalizedSocialClaimCarouselIndex] || null;
    const showSocialClaimCode = (
        modeIsClaimDiscount
        && status === AUTH_ACTION_STATUS_TYPE.success
        && !!socialClaimPreview?.discountCode
    );
    const showSocialClaimList = (
        modeIsClaimDiscount
        && status === AUTH_ACTION_STATUS_TYPE.success
        && socialClaimList.length > 0
    );

    useEffect(() => {
        setSocialClaimCarouselIndex(0);
    }, [socialClaimList.length]);

    useEffect(() => {
        if (!socialClaimNeedsPolling) return undefined;

        let active = true;
        let requestInFlight = false;

        const pollSocialClaimPreview = async (): Promise<void> => {
            const documentIsHidden = (
                typeof document !== 'undefined'
                && document.hidden
            );
            if (
                requestInFlight
                || documentIsHidden
            ) {
                return;
            }

            requestInFlight = true;

            try {
                const claimPreview = await getSocialAutomationClaimPreview({
                    claimLookupToken: claimLookupToken || undefined,
                    claimToken: claimToken || undefined,
                    platform: claimPlatform || undefined,
                });

                if (!active) return;

                setSocialClaimPreview(claimPreview);

                if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.eligibleClaims) {
                    setReadyErrorMessage('');
                    setStatus(AUTH_ACTION_STATUS_TYPE.success);
                    setStatusHeader('Your codes are ready');
                    setStatusMessage('We verified your YouTube account. Use any active code below before it expires.');
                    return;
                }

                if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.verified) {
                    setReadyErrorMessage('');
                    setStatus(AUTH_ACTION_STATUS_TYPE.success);
                    setStatusHeader('Your code is ready');
                    setStatusMessage(getVerifiedSocialClaimMessage(claimPreview));
                    return;
                }

                if (claimPreview.status === SOCIAL_AUTOMATION_CLAIM_PREVIEW_STATUS.noClaims) {
                    setReadyErrorMessage('');
                    setStatus(AUTH_ACTION_STATUS_TYPE.error);
                    setStatusHeader('No active claims found');
                    setStatusMessage(STATUS_MESSAGE_SOCIAL_CLAIM_NO_CLAIMS);
                }
            } finally {
                requestInFlight = false;
            }
        };

        const intervalId = window.setInterval((): void => {
            pollSocialClaimPreview().catch((): void => {
                // Retry on the next interval tick if this request fails.
            });
        }, getSocialClaimPollIntervalMs());

        return () => {
            active = false;
            window.clearInterval(intervalId);
        };
    }, [
        claimLookupToken,
        claimPlatform,
        claimToken,
        modeIsClaimDiscount,
        socialClaimNeedsPolling,
    ]);

    let readyFormContents: JSX.Element;

    if (modeIsClaimBookInvite) {
        readyFormContents = (
            <>
                {!claimBookInviteIsOwnerSelfClaim && (
                    <>
                        <PixelInput
                            customCursorActive={customCursorActive}
                            currentSessionId={currentSessionId}
                            isValid
                            borderColor={theme.color.white}
                            textColor={theme.color.white}
                            focusTextColor={theme.verascopeColor.orange200}
                            invalidTextColor={theme.verascopeColor.red200}
                            placeholderColor={theme.color.white}
                            focusPlaceholderColor={theme.verascopeColor.orange200}
                            invalidPlaceholderColor={theme.verascopeColor.red200}
                            headingColor={theme.color.white}
                            invalidHeadingColor={theme.verascopeColor.red200}
                            inputBackgroundColor="transparent"
                            value={claimFirstName}
                            placeholderText="First Name"
                            hasSound={hasSound}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            autoComplete="given-name"
                            onChange={(value: string) => {
                                setClaimFirstName(value);
                                if (readyErrorMessage.length > 0) {
                                    setReadyErrorMessage('');
                                }
                            }}
                        />
                        <PixelInput
                            customCursorActive={customCursorActive}
                            currentSessionId={currentSessionId}
                            isValid
                            borderColor={theme.color.white}
                            textColor={theme.color.white}
                            focusTextColor={theme.verascopeColor.orange200}
                            invalidTextColor={theme.verascopeColor.red200}
                            placeholderColor={theme.color.white}
                            focusPlaceholderColor={theme.verascopeColor.orange200}
                            invalidPlaceholderColor={theme.verascopeColor.red200}
                            headingColor={theme.color.white}
                            invalidHeadingColor={theme.verascopeColor.red200}
                            inputBackgroundColor="transparent"
                            value={claimLastName}
                            placeholderText="Last Name"
                            hasSound={hasSound}
                            onCursorEnter={onCursorEnter}
                            onCursorLeave={onCursorLeave}
                            setInputFocused={setInputFocused}
                            autoComplete="family-name"
                            onChange={(value: string) => {
                                setClaimLastName(value);
                                if (readyErrorMessage.length > 0) {
                                    setReadyErrorMessage('');
                                }
                            }}
                        />
                    </>
                )}
                <ActionContainer>
                    <Button
                        id="auth-action-claim-button"
                        className={HOVER_TARGET_CLASSNAME}
                        center
                        customCursorActive={customCursorActive}
                        text={claimBookInviteIsOwnerSelfClaim ? 'CLAIM TO MY ACCOUNT' : 'CLAIM BOOK INVITE'}
                        type={BUTTON_TYPE.solid}
                        background={claimBookInviteButtonFocused ? theme.verascopeColor.orange200 : theme.color.white}
                        style={ACTION_BUTTON_TEXT_STYLE(claimBookInviteButtonFocused)}
                        width={ACTION_BUTTON_WIDTH}
                        height={ACTION_BUTTON_HEIGHT}
                        loading={claimSubmitting}
                        disabled={claimBookInviteSubmitDisabled}
                        {...(!claimBookInviteSubmitDisabled
                            ? {
                                onMouseEnter: onResetButtonEnter,
                                onMouseLeave: onResetButtonLeave,
                            }
                            : {}
                        )}
                        {...getPressEventHandlers({ onMouseDown: submitClaimBookInvite })}
                    />
                </ActionContainer>
            </>
        );
    } else if (modeIsClaimDiscount) {
        readyFormContents = (
            <>
                <ClaimDetailsContainer>
                    {!!socialClaimPreview?.commenterUsername && (
                        <ClaimDetailItem>
                            <ClaimDetailLabel>Commenter</ClaimDetailLabel>
                            <ClaimDetailValue>{socialClaimPreview.commenterUsername}</ClaimDetailValue>
                        </ClaimDetailItem>
                    )}
                    {!!socialClaimPreview?.platform && (
                        <ClaimDetailItem>
                            <ClaimDetailLabel>Platform</ClaimDetailLabel>
                            <ClaimDetailValue>{formatSocialClaimPlatform(socialClaimPreview.platform)}</ClaimDetailValue>
                        </ClaimDetailItem>
                    )}
                    {!!socialClaimExpiry && (
                        <ClaimDetailItem>
                            <ClaimDetailLabel>Expires</ClaimDetailLabel>
                            <ClaimDetailValue>{socialClaimExpiry}</ClaimDetailValue>
                        </ClaimDetailItem>
                    )}
                </ClaimDetailsContainer>
                <ActionContainer>
                    <Button
                        id="auth-action-claim-button"
                        className={HOVER_TARGET_CLASSNAME}
                        center
                        customCursorActive={customCursorActive}
                        text="VERIFY"
                        type={BUTTON_TYPE.solid}
                        background={claimDiscountButtonFocused ? theme.verascopeColor.orange200 : theme.color.white}
                        style={ACTION_BUTTON_TEXT_STYLE(claimDiscountButtonFocused)}
                        width={ACTION_BUTTON_WIDTH}
                        height={ACTION_BUTTON_HEIGHT}
                        loading={claimSubmitting}
                        disabled={claimDiscountSubmitDisabled}
                        {...(!claimDiscountSubmitDisabled
                            ? {
                                onMouseEnter: onResetButtonEnter,
                                onMouseLeave: onResetButtonLeave,
                            }
                            : {}
                        )}
                        {...getPressEventHandlers({ onMouseDown: submitClaimDiscountVerification })}
                    />
                </ActionContainer>
            </>
        );
    } else {
        readyFormContents = (
            <>
                <PixelInput
                    customCursorActive={customCursorActive}
                    currentSessionId={currentSessionId}
                    isValid={!passwordInputInvalid}
                    borderColor={theme.color.white}
                    focusBorderColor={theme.verascopeColor.orange200}
                    invalidBorderColor={theme.verascopeColor.red200}
                    textColor={theme.color.white}
                    focusTextColor={theme.verascopeColor.orange200}
                    invalidTextColor={theme.verascopeColor.red200}
                    placeholderColor={theme.color.white}
                    focusPlaceholderColor={theme.verascopeColor.orange200}
                    invalidPlaceholderColor={theme.verascopeColor.red200}
                    headingColor={theme.color.white}
                    invalidHeadingColor={theme.verascopeColor.red200}
                    inputBackgroundColor="transparent"
                    value={password}
                    placeholderText="New Password"
                    hasSound={hasSound}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    inputType="password"
                    autoComplete="new-password"
                    onChange={(value: string) => {
                        setPassword(value);
                        if (readyErrorMessage.length > 0) {
                            setReadyErrorMessage('');
                        }
                        if (passwordErrors.length > 0) {
                            setPasswordErrors([]);
                        }
                    }}
                />
                {passwordStrength && (
                    <PasswordStrengthContainer>
                        <PasswordStrengthBar
                            hasErrors={passwordInputInvalid}
                            active={
                                passwordStrength === PASSWORD_STRENGTH.veryWeak
                                || passwordStrength === PASSWORD_STRENGTH.weak
                                || passwordStrength === PASSWORD_STRENGTH.reasonable
                                || passwordStrength === PASSWORD_STRENGTH.strong
                                || passwordStrength === PASSWORD_STRENGTH.veryStrong
                            }
                        />
                        <PasswordStrengthBar
                            hasErrors={passwordInputInvalid}
                            active={
                                passwordStrength === PASSWORD_STRENGTH.weak
                                || passwordStrength === PASSWORD_STRENGTH.reasonable
                                || passwordStrength === PASSWORD_STRENGTH.strong
                                || passwordStrength === PASSWORD_STRENGTH.veryStrong
                            }
                        />
                        <PasswordStrengthBar
                            hasErrors={passwordInputInvalid}
                            active={
                                passwordStrength === PASSWORD_STRENGTH.reasonable
                                || passwordStrength === PASSWORD_STRENGTH.strong
                                || passwordStrength === PASSWORD_STRENGTH.veryStrong
                            }
                        />
                        <PasswordStrengthBar
                            hasErrors={passwordInputInvalid}
                            active={
                                passwordStrength === PASSWORD_STRENGTH.strong
                                || passwordStrength === PASSWORD_STRENGTH.veryStrong
                            }
                        />
                        <PasswordStrengthBar
                            hasErrors={passwordInputInvalid}
                            active={passwordStrength === PASSWORD_STRENGTH.veryStrong}
                        />
                    </PasswordStrengthContainer>
                )}
                <PixelInput
                    customCursorActive={customCursorActive}
                    currentSessionId={currentSessionId}
                    isValid={!passwordConfirmationInputInvalid}
                    borderColor={theme.color.white}
                    focusBorderColor={theme.verascopeColor.orange200}
                    invalidBorderColor={theme.verascopeColor.red200}
                    textColor={theme.color.white}
                    focusTextColor={theme.verascopeColor.orange200}
                    invalidTextColor={theme.verascopeColor.red200}
                    placeholderColor={theme.color.white}
                    focusPlaceholderColor={theme.verascopeColor.orange200}
                    invalidPlaceholderColor={theme.verascopeColor.red200}
                    headingColor={theme.color.white}
                    invalidHeadingColor={theme.verascopeColor.red200}
                    inputBackgroundColor="transparent"
                    value={passwordConfirmation}
                    placeholderText="Confirm New Password"
                    hasSound={hasSound}
                    onCursorEnter={onCursorEnter}
                    onCursorLeave={onCursorLeave}
                    setInputFocused={setInputFocused}
                    inputType="password"
                    autoComplete="new-password"
                    onChange={(value: string) => {
                        setPasswordConfirmation(value);
                        if (readyErrorMessage.length > 0) {
                            setReadyErrorMessage('');
                        }
                    }}
                />
                {showResetPasswordSubmitButton && (
                    <ActionContainer>
                        <Button
                            className={HOVER_TARGET_CLASSNAME}
                            center
                            customCursorActive={customCursorActive}
                            text={submitting ? 'UPDATING...' : 'UPDATE PASSWORD'}
                            type={BUTTON_TYPE.solid}
                            background={resetPasswordButtonFocused ? theme.verascopeColor.orange200 : theme.color.white}
                            style={ACTION_BUTTON_TEXT_STYLE(resetPasswordButtonFocused)}
                            width={ACTION_BUTTON_WIDTH}
                            height={ACTION_BUTTON_HEIGHT}
                            loading={submitting}
                            disabled={resetPasswordSubmitDisabled}
                            {...(!resetPasswordSubmitDisabled
                                ? {
                                    onMouseEnter: onResetButtonEnter,
                                    onMouseLeave: onResetButtonLeave,
                                }
                                : {}
                            )}
                            {...getPressEventHandlers({ onMouseDown: submitResetPassword })}
                        />
                    </ActionContainer>
                )}
            </>
        );
    }

    return (
        <Container
            hasFailure={status === AUTH_ACTION_STATUS_TYPE.error}
            hasSuccess={status === AUTH_ACTION_STATUS_TYPE.success}
        >
            <PageLogo
                dark
                withEnterTransition
                className={HOVER_TARGET_CLASSNAME}
                customCursorActive={customCursorActive}
                {...getTouchMouseEventHandlers({
                    onMouseEnter: (e) => onLogoEnter(e),
                    onMouseLeave: (e) => onLogoLeave(e),
                })}
                {...getPressEventHandlers({ onMouseDown: onLogoClick })}
            >
                <ReactSVG
                    src={VerascopeLogo}
                />
            </PageLogo>
            <PageTitle
                withEnterTransition
                color={theme.verascopeColor.purple200}
            >
                {authActionPageCopy.title}
            </PageTitle>
            <PageSubtitle
                withEnterTransition
                color={theme.verascopeColor.purple400}
            >
                {authActionPageCopy.subtitle}
            </PageSubtitle>
            <ContentContainer
                hasFailure={status === AUTH_ACTION_STATUS_TYPE.error}
                hasSuccess={status === AUTH_ACTION_STATUS_TYPE.success}
            >
                <LogomarkContainer>
                    <ReactSVG
                        src={VerascopeLogomark}
                    />
                </LogomarkContainer>
                <StatusHeader data-testid="auth-action-status-header">
                    {statusHeader}
                </StatusHeader>
                <StatusMessage
                    data-testid="auth-action-status-message"
                    hasFailure={hasStatusFailure}
                    hasSuccess={status === AUTH_ACTION_STATUS_TYPE.success}
                >
                    {status === AUTH_ACTION_STATUS_TYPE.ready && readyErrorMessage.length > 0
                        ? readyErrorMessage
                        : statusMessage}
                    {status === AUTH_ACTION_STATUS_TYPE.ready && readyErrorMessage.length === 0 && linkedEmail.length > 0
                        ? ` (${linkedEmail})`
                        : ''}
                </StatusMessage>
                {status === AUTH_ACTION_STATUS_TYPE.loading && (
                    <LoadingContainer>
                        <Spinner large lightBackground />
                    </LoadingContainer>
                )}
                {status === AUTH_ACTION_STATUS_TYPE.ready && (
                    <FormContainer
                        data-testid="auth-action-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (modeIsClaimDiscount) {
                                submitClaimDiscountVerification();
                                return;
                            }
                            if (modeIsClaimBookInvite) {
                                submitClaimBookInvite();
                                return;
                            }
                            submitResetPassword();
                        }}
                    >
                        {readyFormContents}
                    </FormContainer>
                )}
                {showSocialClaimCode && (
                    <ClaimDetailsContainer>
                        <ClaimCode data-testid="auth-action-claim-code">
                            {socialClaimPreview?.discountCode}
                        </ClaimCode>
                        {!!socialClaimPreview?.commenterUsername && (
                            <ClaimDetailItem>
                                <ClaimDetailLabel>Commenter</ClaimDetailLabel>
                                <ClaimDetailValue>{socialClaimPreview.commenterUsername}</ClaimDetailValue>
                            </ClaimDetailItem>
                        )}
                        {!!socialClaimPreview?.platform && (
                            <ClaimDetailItem>
                                <ClaimDetailLabel>Platform</ClaimDetailLabel>
                                <ClaimDetailValue>{formatSocialClaimPlatform(socialClaimPreview.platform)}</ClaimDetailValue>
                            </ClaimDetailItem>
                        )}
                        {!!socialClaimExpiry && (
                            <ClaimDetailItem>
                                <ClaimDetailLabel>Expires</ClaimDetailLabel>
                                <ClaimDetailValue>{socialClaimExpiry}</ClaimDetailValue>
                            </ClaimDetailItem>
                        )}
                    </ClaimDetailsContainer>
                )}
                {showSocialClaimList && (
                    <ClaimDetailsContainer>
                        {!!activeSocialClaim && (
                            <ClaimCarouselContainer>
                                <ClaimCarouselCard>
                                    <ClaimCode>
                                        {activeSocialClaim.discountCode}
                                    </ClaimCode>
                                    {!!activeSocialClaim.targetTitle && (
                                        <ClaimDetailItem>
                                            <ClaimDetailLabel>Short</ClaimDetailLabel>
                                            <ClaimDetailValue>{activeSocialClaim.targetTitle}</ClaimDetailValue>
                                        </ClaimDetailItem>
                                    )}
                                    {!!activeSocialClaim.triggerKeyword && (
                                        <ClaimDetailItem>
                                            <ClaimDetailLabel>Keyword</ClaimDetailLabel>
                                            <ClaimDetailValue>{activeSocialClaim.triggerKeyword}</ClaimDetailValue>
                                        </ClaimDetailItem>
                                    )}
                                    {!!formatSocialClaimDateTime(activeSocialClaim.commentedAt) && (
                                        <ClaimDetailItem>
                                            <ClaimDetailLabel>Commented</ClaimDetailLabel>
                                            <ClaimDetailValue>
                                                {formatSocialClaimDateTime(activeSocialClaim.commentedAt)}
                                            </ClaimDetailValue>
                                        </ClaimDetailItem>
                                    )}
                                    {!!formatSocialClaimDateTime(activeSocialClaim.expiresAt) && (
                                        <ClaimDetailItem>
                                            <ClaimDetailLabel>Expires</ClaimDetailLabel>
                                            <ClaimDetailValue>
                                                {formatSocialClaimDateTime(activeSocialClaim.expiresAt)}
                                            </ClaimDetailValue>
                                        </ClaimDetailItem>
                                    )}
                                </ClaimCarouselCard>
                                {socialClaimList.length > 1 && (
                                    <>
                                        <ClaimCarouselNavRow>
                                            <ClaimCarouselNavButton
                                                type="button"
                                                className={HOVER_TARGET_CLASSNAME}
                                                aria-label="Previous claim"
                                                customCursorActive={customCursorActive}
                                                onClick={() => setSocialClaimCarouselIndex((previousIndex) => (
                                                    normalizeCarouselIndex(
                                                        previousIndex - 1,
                                                        socialClaimList.length,
                                                    )
                                                ))}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onClaimCarouselControlEnter,
                                                    onMouseLeave: onClaimCarouselControlLeave,
                                                })}
                                            >
                                                PREV
                                            </ClaimCarouselNavButton>
                                            <ClaimCarouselNavButton
                                                type="button"
                                                className={HOVER_TARGET_CLASSNAME}
                                                aria-label="Next claim"
                                                customCursorActive={customCursorActive}
                                                onClick={() => setSocialClaimCarouselIndex((previousIndex) => (
                                                    normalizeCarouselIndex(
                                                        previousIndex + 1,
                                                        socialClaimList.length,
                                                    )
                                                ))}
                                                {...getTouchMouseEventHandlers({
                                                    onMouseEnter: onClaimCarouselControlEnter,
                                                    onMouseLeave: onClaimCarouselControlLeave,
                                                })}
                                            >
                                                NEXT
                                            </ClaimCarouselNavButton>
                                        </ClaimCarouselNavRow>
                                        <ClaimCarouselDots>
                                            {socialClaimList.map((claim, claimIndex) => (
                                                <ClaimCarouselDotButton
                                                    key={`claim-carousel-dot-${claim.discountCode}-${claim.expiresAt}`}
                                                    type="button"
                                                    className={HOVER_TARGET_CLASSNAME}
                                                    aria-label={`Show claim ${claimIndex + 1}`}
                                                    active={claimIndex === normalizedSocialClaimCarouselIndex}
                                                    customCursorActive={customCursorActive}
                                                    onClick={() => {
                                                        setSocialClaimCarouselIndex(claimIndex);
                                                    }}
                                                    {...getTouchMouseEventHandlers({
                                                        onMouseEnter: onClaimCarouselControlEnter,
                                                        onMouseLeave: onClaimCarouselControlLeave,
                                                    })}
                                                />
                                            ))}
                                        </ClaimCarouselDots>
                                    </>
                                )}
                            </ClaimCarouselContainer>
                        )}
                    </ClaimDetailsContainer>
                )}
                {status !== AUTH_ACTION_STATUS_TYPE.loading && status !== AUTH_ACTION_STATUS_TYPE.ready && (
                    <ActionContainer>
                        <Button
                            id="auth-action-footer-button"
                            className={HOVER_TARGET_CLASSNAME}
                            center
                            customCursorActive={customCursorActive}
                            text={footerActionButtonText}
                            type={BUTTON_TYPE.solid}
                            background={footerActionButtonFocused ? theme.verascopeColor.orange200 : theme.color.white}
                            style={ACTION_BUTTON_TEXT_STYLE(footerActionButtonFocused)}
                            width={ACTION_BUTTON_WIDTH}
                            height={ACTION_BUTTON_HEIGHT}
                            {...(!footerActionButtonDisabled
                                ? {
                                    onMouseEnter: onFooterButtonEnter,
                                    onMouseLeave: onFooterButtonLeave,
                                }
                                : {}
                            )}
                            disabled={footerActionButtonDisabled}
                            loading={footerActionButtonDisabled}
                            {...getPressEventHandlers({
                                onMouseDown: () => {
                                    if (status === AUTH_ACTION_STATUS_TYPE.success) {
                                        proceedFromSuccess();
                                        return;
                                    }
                                    if (
                                        mode === AUTH_ACTION_MODE_TYPE.resetPassword
                                        || mode === AUTH_ACTION_MODE_TYPE.legacyResetPassword
                                    ) {
                                        requestNewResetLink();
                                        return;
                                    }
                                    if (mode === AUTH_ACTION_MODE_TYPE.verifyEmail) {
                                        requestNewVerifyLink();
                                        return;
                                    }
                                    navigate(
                                        `/${PAGE_ROUTE.landing}`,
                                        {
                                            state: {
                                                prevPath: location.pathname,
                                            },
                                        },
                                    );
                                },
                            })}
                        />
                    </ActionContainer>
                )}
            </ContentContainer>
        </Container>
    );
}

export default AuthActionView;
